import type { Task } from "@common/types/task";
import { TaskColumn, TaskSortValue } from "@common/types/views/tasksView";
import { compareTasks } from "../compareTasks";
import { DEFAULT_TASK_SORT } from "../taskListConstants";
import { taskViewConfig } from "../taskViewConfig";

/** Every board sort: each field List sorts by, as `TaskColumn` ids. */
export const TASK_BOARD_SORTS = [
    TaskColumn.NAME,
    TaskColumn.DUE_DATE,
    TaskColumn.ESTIMATE,
    TaskColumn.PRIORITY,
    TaskColumn.STATUS,
    TaskColumn.ASSIGNEE,
    TaskColumn.CREATED_AT,
    TaskColumn.CREATED_BY,
    TaskColumn.UPDATED_AT,
    TaskColumn.COMPLETED_AT,
    TaskColumn.COMPLETED_BY,
    TaskColumn.CLIENT,
] as const;

export type TaskBoardSort = (typeof TASK_BOARD_SORTS)[number];

export type TaskBoardSortDirection = "asc" | "desc";

/** A board order: the sort and its direction. */
export interface TaskBoardSortState {
    sort: TaskBoardSort;
    sortDirection: TaskBoardSortDirection;
}

const SORT_DIRECTIONS: TaskBoardSortDirection[] = ["asc", "desc"];

/** List's sort value for a board sort, as its column config declares it. */
const findListSort = ({ sort, sortDirection }: TaskBoardSortState) =>
    taskViewConfig.columns[sort].sort?.[sortDirection];

const BOARD_SORTS_BY_LIST_SORT = new Map<TaskSortValue, TaskBoardSortState>(
    TASK_BOARD_SORTS.flatMap((sort) =>
        SORT_DIRECTIONS.flatMap(
            (sortDirection): Array<[TaskSortValue, TaskBoardSortState]> => {
                const listSort = findListSort({ sort, sortDirection });
                return listSort === undefined
                    ? []
                    : [[listSort, { sort, sortDirection }]];
            },
        ),
    ),
);

/** The board order for a List sort Board has no field for. */
const FIRST_BOARD_SORT: TaskBoardSortState = {
    sort: TASK_BOARD_SORTS[0],
    sortDirection: "asc",
};

/** The board's order: List's sort as a board field and direction; undefined is List's default. */
export const getTaskBoardSort = (
    listSort: TaskSortValue | undefined = DEFAULT_TASK_SORT,
): TaskBoardSortState =>
    BOARD_SORTS_BY_LIST_SORT.get(listSort) ?? FIRST_BOARD_SORT;

/** List's sort value for a board order; List's default if its column has none. */
const toListSort = (boardSort: TaskBoardSortState): TaskSortValue =>
    findListSort(boardSort) ?? DEFAULT_TASK_SORT;

/**
 * List's sort after a sort change on Board. A newly picked field starts
 * ascending; a direction change, or the same field again, keeps the other.
 */
export const getTaskSortValue = (
    change: Partial<TaskBoardSortState>,
    listSort: TaskSortValue | undefined,
): TaskSortValue => {
    const current = getTaskBoardSort(listSort);
    const sort = change.sort ?? current.sort;
    const sortDirection =
        change.sortDirection ??
        (sort === current.sort ? current.sortDirection : "asc");
    return toListSort({ sort, sortDirection });
};

/** A board sort's name, as the sort hint and the Display popover say it: List's column header. */
export const getTaskBoardSortLabel = (sort: TaskBoardSort): string =>
    taskViewConfig.columns[sort].name;

/** Orders two tasks, as `Array.prototype.sort` takes it. */
export type TaskComparator = (first: Task, second: Task) => number;

interface TaskBoardComparatorArgs {
    sort: TaskBoardSort;
    direction: TaskBoardSortDirection;
    /** Names clients for the Client sort. */
    clientNamesById: ReadonlyMap<string, string>;
}

/** The card order for a board sort: List's own comparator, ties on task id. */
export const getTaskBoardComparator = ({
    sort,
    direction,
    clientNamesById,
}: TaskBoardComparatorArgs): TaskComparator =>
    compareTasks({
        sort: toListSort({ sort, sortDirection: direction }),
        clientNamesById,
    });
